import React from 'react';
import './Greeting.scss';
import { GreetingSchema } from '../../types/types';
import DOMPurify from 'dompurify';
import { FilterKAI, IconPendoInfo, IconClose, FilterAnomaly } from 'components/Icons';

interface GreetingProps {
  data?: GreetingSchema;
  onViewReport?: () => void;
  isReportVisible?: boolean;
  children?: React.ReactNode;
}

/**
 * Format URLs in text to be clickable links, enhance line breaks, and style first line
 * @param text Text that may contain URLs
 * @returns Text with URLs wrapped in anchor tags, enhanced line breaks, and styled first line
 */
const formatUrlsInText = (text: string): string => {
  if (!text) return '';

  // First, replace <br> tags with a custom styled spacer
  let formattedText = text.replace(/<br\s*\/?>/gi, '<div class="line-spacer"></div>');

  // Style the first line by wrapping it in a span with a special class
  // Find the first occurrence of a line spacer or end of string
  const firstLineBreakIndex = formattedText.indexOf('<div class="line-spacer"></div>');

  if (firstLineBreakIndex !== -1) {
    // There is at least one line break, wrap the first line
    const firstLine = formattedText.substring(0, firstLineBreakIndex);
    const restOfText = formattedText.substring(firstLineBreakIndex);
    formattedText = `<span class="first-line">${firstLine}</span>${restOfText}`;
  } else {
    // No line breaks, the entire text is the first line
    formattedText = `<span class="first-line">${formattedText}</span>`;
  }

  // Regex pattern to match URLs
  const urlPattern =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;

  return formattedText.replace(urlPattern, url => {
    // If the URL is already wrapped in an <a> tag, return it as is
    if (url.startsWith('<a') && url.endsWith('</a>')) {
      return url;
    }

    // Otherwise, wrap it in an <a> tag
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

const Greeting: React.FC<GreetingProps> = ({ data, onViewReport, isReportVisible, children }) => {
  // If data is undefined, provide default values
  if (!data) {
    return (
      <div className="ai-greeting">
        <div className="ai-greeting__message">No greeting data available. Please refresh the analysis.</div>
      </div>
    );
  }

  const { salutation, message, callToAction, reportAvailable, reportPreview } = data;

  // Configure DOMPurify for safe HTML rendering
  const purifyConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ADD_ATTR: ['target', 'rel'],
    KEEP_CONTENT: true,
  };

  // Sanitize and format the message content
  const sanitizedMessage = formatUrlsInText(DOMPurify.sanitize(message || '', purifyConfig));
  const parsedMessage = { __html: sanitizedMessage };

  // Sanitize and format the preview content
  const sanitizedPreview = formatUrlsInText(DOMPurify.sanitize(reportPreview || '', purifyConfig));
  const parsedPreview = { __html: sanitizedPreview };

  return (
    <div className="ai-greeting">
      <div className="ai-greeting__message-container">
        <div className="ai-greeting__message-container__avatar">
          <FilterKAI />
        </div>
        <div className="ai-greeting__message-container__message-block">
          <div className="ai-greeting__message-container__user">{salutation}</div>
          <div className="ai-greeting__message-container__message" dangerouslySetInnerHTML={parsedMessage} />
          <div className="ai-greeting__message-container__call-to-action">{callToAction}</div>
        </div>
      </div>
      {reportAvailable && (
        <div className={isReportVisible ? 'ai-greeting__report showing-report' : 'ai-greeting__report'}>
          {onViewReport && (
            <div
              className="ai-greeting__report-preview-control"
              onClick={onViewReport}
              role="button"
              tabIndex={0}
              aria-expanded={isReportVisible}
              aria-controls="ai-analysis-detailed-report"
              onKeyPress={e => e.key === 'Enter' && onViewReport()}
            >
              <div className="ai-greeting__report-preview-chart">
                <FilterAnomaly />
              </div>
              <div className="ai-greeting__report-preview-text" dangerouslySetInnerHTML={parsedPreview} />
              {isReportVisible && (
                <div className="ai-greeting__report-preview-toggle">
                  <IconClose />
                </div>
              )}
            </div>
          )}
          {isReportVisible && <div className="ai-greeting__report-content">{children}</div>}
        </div>
      )}
    </div>
  );
};

export default Greeting;
